import { getLocalTimeZone, parseAbsoluteToLocal, parseDateTime } from "@internationalized/date";
import type { ClipboardEventHandler, ReactNode } from "react";
import { forwardRef, useContext } from "react";
import type { DateValue, DatePickerProps as ReactAriaDateFieldProps } from "react-aria-components";
import {
  Calendar,
  CalendarCell,
  CalendarGrid,
  DatePickerStateContext,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
  DateInput as ReactAriaDateInput,
  DatePicker as ReactAriaDatePicker,
} from "react-aria-components";
import { FieldError } from "react-hook-form";

import { cn, type VariantProps } from "../utils";
import { NcButton, ncButtonClasses } from "./nc-button";
import { NcFieldLayout, NcFieldLayoutProps } from "./nc-field-layout";
import { NcIconCalendar, NcIconChevronLeft, NcIconChevronRight } from "./nc-icons";
import { ncInputClasses, ncInputWidthVariants } from "./nc-input";
import { NcLabel } from "./nc-label";

const PasteHandler = ({ children }: { children: ReactNode }) => {
  const { setValue } = useContext(DatePickerStateContext) as {
    setValue: (value: DateValue) => void;
  };

  const onPaste = (e: ClipboardEvent) => {
    const pastedData = e.clipboardData?.getData("text");
    if (pastedData) {
      const normaliseString = pastedData.replaceAll(/[^0-9]/g, "");
      const parts = {
        day: normaliseString.slice(0, 2),
        month: normaliseString.slice(2, 4),
        year: normaliseString.slice(4, 8),
        hour: normaliseString.slice(8, 10),
        minute: normaliseString.slice(10, 12),
      };
      const dateString = `${parts.year}-${parts.month}-${parts.day}`;
      setValue(
        parts.hour && parts.minute
          ? parseDateTime(`${dateString}T${parts.hour}:${parts.minute}`)
          : parseDateTime(dateString)
      );
    }
  };
  return (
    <div
      tabIndex={-1}
      className="contents"
      onPaste={onPaste as unknown as ClipboardEventHandler<HTMLDivElement>}
    >
      {children}
    </div>
  );
};

export const PickerCalendar = () => {
  return (
    <Popover className="bg-light border-ui focus-visible:outline-active rounded-md border px-4 py-5 shadow-lg">
      <Dialog>
        <Calendar>
          <header className="border-ui mb-2 flex items-center justify-between border-b pb-2">
            <NcButton variant="icon" slot="previous">
              <NcIconChevronLeft />
            </NcButton>
            <Heading />
            <NcButton variant="icon" slot="next">
              <NcIconChevronRight />
            </NcButton>
          </header>

          <CalendarGrid className="focus-visible:outline-active min-h-60 text-center [&_th]:py-2">
            {date => (
              <CalendarCell
                date={date}
                className={cn(
                  ncButtonClasses.base,
                  ncButtonClasses.variant.default,
                  "not-data-pressed:not-data-hovered:not-data-selected:data-today:bg-ui/50 inline border-none data-disabled:bg-transparent"
                )}
              />
            )}
          </CalendarGrid>
        </Calendar>
      </Dialog>
    </Popover>
  );
};

export interface NcDateFieldProps
  extends VariantProps<typeof ncInputWidthVariants>,
    ReactAriaDateFieldProps<DateValue> {
  showPicker?: boolean;
  label: string;
  hideLabel?: boolean;
  description?: string;
  error?: FieldError;
  variant?: NcFieldLayoutProps["variant"];
}

export const NcDateField = forwardRef<HTMLInputElement, NcDateFieldProps>(
  (
    {
      label,
      hideLabel,
      description,
      error,
      isRequired,
      showPicker,
      inputWidth = "auto",
      shouldForceLeadingZeros = true,
      hourCycle = 24,
      variant,
      ...props
    },
    ref
  ) => {
    return (
      <ReactAriaDatePicker
        data-nc="NcDateField"
        shouldForceLeadingZeros={shouldForceLeadingZeros}
        hourCycle={hourCycle}
        {...props}
      >
        <NcFieldLayout variant={variant}>
          <NcLabel isRequired={isRequired} hideLabel={hideLabel}>
            {label}
          </NcLabel>
          <PasteHandler>
            <Group
              className={cn(
                ncInputClasses,
                "data-focus-within:not-data-invalid:border-active flex items-center gap-2 data-focus-within:outline-none",
                ncInputWidthVariants({ inputWidth })
              )}
            >
              <ReactAriaDateInput className="flex grow gap-1" ref={ref}>
                {segment => (
                  <DateSegment
                    className="data-focused:outline-main data-focused:outline-2 data-focused:outline-offset-2"
                    segment={segment}
                  />
                )}
              </ReactAriaDateInput>
              {showPicker && (
                <NcButton variant="icon" className="justify-self-end">
                  <NcIconCalendar className="h-5" />
                </NcButton>
              )}
            </Group>
            {showPicker && <PickerCalendar />}
          </PasteHandler>
          {description && <NcFieldLayout.Description>{description}</NcFieldLayout.Description>}
          <NcFieldLayout.ErrorMessage>{error?.message}</NcFieldLayout.ErrorMessage>
        </NcFieldLayout>
      </ReactAriaDatePicker>
    );
  }
);

export const dateValueToDate = (dateValue: DateValue | undefined) => {
  return dateValue ? dateValue.toDate(getLocalTimeZone()) : undefined;
};

export const dateTimeToDateValue = (date: Date | undefined) => {
  return date ? parseAbsoluteToLocal(date.toISOString()) : null;
};
